import { c as _c } from "react/compiler-runtime";
import { Chip, Stack } from '@mui/material';
import type { ScanJob, BackupJob, RestoreJob } from '@repo/api-gw-sdk';
import { BackupJobType, RestoreJobType, ScanJobType } from '@repo/api-gw-sdk';
import { ClickableText } from '@/components/shared/clickableText';
import type { AppRow, AppTable } from '@/components/table/tanstackTable';
export const restoreJobTypeMapper: Record<RestoreJobType, string> = {
  [RestoreJobType.RestoreJobTypeUnspecified]: 'Restore Job',
  [RestoreJobType.AwsS3ObjectRestore]: 'S3 file restore',
  [RestoreJobType.AwsS3BucketRestore]: 'S3 bucket restore',
  [RestoreJobType.AwsEc2FileRestore]: 'EC2 file restore',
  [RestoreJobType.AwsEc2InstanceRestore]: 'EC2 instance restore',
  [RestoreJobType.AwsEc2AmiConvert]: 'EC2 AMI conversion',
  [RestoreJobType.AwsEc2EbsVolumeRestore]: 'EBS volume restore',
  [RestoreJobType.AwsEc2EbsSnapshotConvert]: 'EBS snapshot conversion',
  [RestoreJobType.AwsEbsVolumeRestore]: 'EBS volume restore',
  [RestoreJobType.AwsEbsSnapshotConvert]: 'EBS snapshot conversion',
  [RestoreJobType.AwsRdsInstanceRestore]: 'RDS instance restore',
  [RestoreJobType.AwsEksNamespaceRestore]: 'EKS Namespace restore',
  [RestoreJobType.AwsEksFileRestore]: 'EKS file restore',
  [RestoreJobType.AwsFsxRestore]: 'FSx restore',
  [RestoreJobType.AwsDocumentdbRestore]: 'DocumentDB restore',
  [RestoreJobType.AwsNeptuneRestore]: 'Neptune restore',
  [RestoreJobType.AwsDynamoDbTableRestore]: 'DynamoDB table restore',
  [RestoreJobType.AwsDynamoDbTableToExistingTableRestore]: 'DynamoDB restore to existing table',
  [RestoreJobType.AwsDynamoDbRestoreToS3]: 'DynamoDB restore to S3',
  [RestoreJobType.AwsEc2RecordRestore]: 'EC2 record restore',
  [RestoreJobType.AwsDynamoDbRecordRestore]: 'DynamoDB record restore',
  [RestoreJobType.AwsKeyspacesTableRestore]: 'Keyspaces table restore',
  [RestoreJobType.AwsKeyspacesRecordRestore]: 'Keyspaces record restore',
  [RestoreJobType.AwsCassandraClusterRestore]: 'Cassandra cluster restore',
  [RestoreJobType.AwsOpensearchDomainRestore]: 'OpenSearch domain restore',
  [RestoreJobType.AwsEksNamespaceRecordRestore]: 'EKS Namespace record restore',
  [RestoreJobType.AwsRdsRecordRestore]: 'RDS record restore',
  [RestoreJobType.AzureStorageAccountRestore]: 'Storage account restore',
  [RestoreJobType.AzureStorageAccountBlobRestore]: 'Storage account file restore',
  [RestoreJobType.AzureVmFileRestore]: 'VM file restore',
  [RestoreJobType.AzureVmDiskRestore]: 'VM disk restore',
  [RestoreJobType.AzureVmInstanceRestore]: 'VM instance restore',
  [RestoreJobType.AzureVmRecordRestore]: 'VM record restore',
  [RestoreJobType.AzureSqlDatabaseRecordRestore]: 'SQL Database record restore',
  [RestoreJobType.AzureSqlDatabaseRestore]: 'SQL Database restore',
  [RestoreJobType.AzureSqlVmInstanceRestore]: 'SQL Virtual Machine restore',
  [RestoreJobType.AzureSqlVmRecordRestore]: 'SQL Virtual Machine record restore',
  [RestoreJobType.AzurePostgresqlRecordRestore]: 'PostgreSQL record restore',
  [RestoreJobType.AzureSapHanaVmInstanceRestore]: 'SAP HANA VM restore',
  [RestoreJobType.GcpSapHanaVmInstanceRestore]: 'SAP HANA VM restore',
  [RestoreJobType.GcpCloudStorageBucketRestore]: 'GCS bucket restore',
  [RestoreJobType.GcpCloudStorageObjectRestore]: 'GCS file restore',
  [RestoreJobType.GcpComputeEngineFileRestore]: 'GCE file restore',
  [RestoreJobType.GcpComputeEngineDiskRestore]: 'GCE disk restore',
  [RestoreJobType.GcpComputeEngineInstanceRestore]: 'GCE instance restore',
  [RestoreJobType.GcpCloudSqlDatabaseRestore]: 'Cloud SQL instance restore',
  [RestoreJobType.GcpCloudSqlRecordRestore]: 'Cloud SQL record restore',
  [RestoreJobType.GcpComputeEngineRecordRestore]: 'GCE record restore',
  [RestoreJobType.GcpBigqueryDatasetRestore]: 'BigQuery restore',
  [RestoreJobType.GcpBigqueryRecordRestore]: 'BigQuery record restore',
  [RestoreJobType.GcpBigqueryNativeRestoreDataset]: 'BigQuery PITR restore',
  [RestoreJobType.GcpBigqueryNativeRestoreTable]: 'BigQuery PITR restore',
  [RestoreJobType.RdsNativePitrRestore]: 'RDS native PITR restore',
  [RestoreJobType.AtlasClusterRecordRestore]: 'MongoDB Atlas cluster record restore',
  [RestoreJobType.AtlasClusterRestore]: 'MongoDB Atlas cluster restore',
  [RestoreJobType.ExportAccessDeniedItems]: 'Export access-denied items',
  [RestoreJobType.Microsoft365Restore]: 'Microsoft 365 Restore',
  [RestoreJobType.GoogleWorkspaceRestore]: 'Google Workspace Restore'
};
export const backupJobTypeMapper: Record<BackupJobType, string> = {
  [BackupJobType.BackupJobTypeUnspecified]: 'Backup Job',
  [BackupJobType.AwsEc2Backup]: 'EC2 backup',
  [BackupJobType.AwsEbsVolumeBackup]: 'EBS volume backup',
  [BackupJobType.AwsEc2SnapshotConversion]: 'EC2 snapshot conversion',
  [BackupJobType.AwsRdsBackup]: 'RDS backup',
  [BackupJobType.AwsS3Backup]: 'S3 backup',
  [BackupJobType.AwsS3LlgBackup]: 'Log space ingestion',
  [BackupJobType.AwsFsxBackup]: 'FSx backup',
  [BackupJobType.AwsEfsBackup]: 'EFS backup',
  [BackupJobType.AwsDocumentdbBackup]: 'DocumentDB backup',
  [BackupJobType.AwsNeptuneBackup]: 'Neptune backup',
  [BackupJobType.AwsDynamoDbBackup]: 'DynamoDB backup',
  [BackupJobType.AwsDynamoDbBackupFull]: 'DynamoDB full scan',
  [BackupJobType.AwsDynamoDbBackupFullExport]: 'DynamoDB full export',
  [BackupJobType.AwsEksNamespaceBackup]: 'EKS Namespace backup',
  [BackupJobType.AwsEksClusterBackup]: 'EKS Cluster backup',
  [BackupJobType.AwsKeyspacesBackupFull]: 'Keyspaces full scan',
  [BackupJobType.AwsCassandraClusterBackup]: 'Cassandra cluster backup',
  [BackupJobType.AwsOpensearchBackup]: 'OpenSearch backup',
  [BackupJobType.AzureStorageAccountBackup]: 'Storage account backup',
  [BackupJobType.AzureVmBackup]: 'VM backup',
  [BackupJobType.AzureVmRestorePointConversion]: 'VM backup conversion',
  [BackupJobType.AzureSqlVirtualMachineBackup]: 'SQL Virtual Machine backup',
  [BackupJobType.AzureSqlVirtualMachineBackupFull]: 'SQL Virtual Machine full backup',
  [BackupJobType.AzureSqlDatabaseBackup]: 'SQL database backup',
  [BackupJobType.AzureSqlManagedInstanceBackup]: 'SQL managed instance backup',
  [BackupJobType.AzurePostgresqlBackup]: 'PostgreSQL backup',
  [BackupJobType.AzureMysqlBackup]: 'MySQL backup',
  [BackupJobType.GcpComputeEngineInstanceBackup]: 'GCE instance backup',
  [BackupJobType.GcpDiskBackup]: 'GCE disk backup',
  [BackupJobType.GcpCloudStorageBackup]: 'GCS backup',
  [BackupJobType.GcpCloudSqlBackup]: 'Cloud SQL backup',
  [BackupJobType.GcpBigqueryBackup]: 'BigQuery backup',
  [BackupJobType.GcpSapHanaVmBackup]: 'SAP HANA VM backup',
  [BackupJobType.AzureSapHanaVmBackup]: 'SAP HANA VM backup',
  [BackupJobType.AtlasMongodbClusterBackup]: 'MongoDB cluster backup',
  [BackupJobType.AtlasMongodbClusterBackupFull]: 'MongoDB cluster full scan',
  [BackupJobType.GoogleWorkspaceBackup]: 'Google Workspace backup',
  [BackupJobType.Microsoft365Backup]: 'Microsoft 365 backup'
};
export const scanJobTypeMapper: Record<ScanJobType, string> = {
  [ScanJobType.ScanJobTypeUnspecified]: 'Classification Job',
  [ScanJobType.AwsEc2Scan]: 'EC2 classification',
  [ScanJobType.AwsEbsVolumeScan]: 'EBS volume classification',
  [ScanJobType.AwsRdsScan]: 'RDS classification',
  [ScanJobType.AwsS3Scan]: 'S3 classification',
  [ScanJobType.AwsDynamoDbScan]: 'DynamoDB classification',
  [ScanJobType.AwsEksNamespaceScan]: 'EKS Namespace classification',
  [ScanJobType.AzureStorageAccountScan]: 'Storage account classification',
  [ScanJobType.AzureVmScan]: 'VM classification',
  [ScanJobType.AzureSqlDatabaseScan]: 'SQL database classification',
  [ScanJobType.AzureSqlVirtualMachineScan]: 'SQL Virtual Machine classification',
  [ScanJobType.GcpComputeEngineInstanceScan]: 'GCE instance classification',
  [ScanJobType.GcpDiskScan]: 'GCE disk classification',
  [ScanJobType.GcpCloudStorageScan]: 'GCS classification',
  [ScanJobType.GcpCloudSqlScan]: 'Cloud SQL classification',
  [ScanJobType.GcpBigqueryScan]: 'BigQuery classification',
  [ScanJobType.AtlasMongodbClusterScan]: 'MongoDB cluster classification',
  [ScanJobType.Microsoft365Scan]: 'Microsoft 365 Scan'
};
export const getJobType = (entity: BackupJob | RestoreJob | ScanJob) => {
  if ('restoreType' in entity) {
    return !entity.restoreType ? 'Restore Job' : restoreJobTypeMapper[entity.restoreType];
  }
  if ('backupType' in entity) {
    return !entity.backupType ? 'Backup Job' : backupJobTypeMapper[entity.backupType];
  }
  if ('scanType' in entity) {
    return !entity.scanType ? 'Classification Job' : scanJobTypeMapper[entity.scanType];
  }
  return;
};
export const isInternalRestoreJob = (entity: BackupJob | RestoreJob | ScanJob) => 'isInternal' in entity && !!entity.isInternal;
export const InternalJobChip = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Chip label="Internal" variant="outlined" size="small" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
export const JobTypeCell = t0 => {
  const $ = _c(9);
  const {
    row,
    table
  } = t0;
  const job = row.original;
  let t1;
  if ($[0] !== row || $[1] !== table) {
    t1 = () => {
      if (row.getIsSelected()) {
        table.resetRowSelection(true);
      } else {
        table.setRowSelection({
          [row.id]: true
        });
      }
    };
    $[0] = row;
    $[1] = table;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const toggleJobsCard = t1;
  let t2;
  if ($[3] !== job) {
    t2 = getJobType(job);
    $[3] = job;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const jobType = t2;
  let t3;
  if ($[5] !== job || $[6] !== jobType || $[7] !== toggleJobsCard) {
    t3 = jobType && <Stack direction="row" sx={{
      alignItems: "center",
      gap: "8px"
    }}><ClickableText onClick={toggleJobsCard}>{jobType}</ClickableText>{isInternalRestoreJob(job) && <InternalJobChip />}</Stack>;
    $[5] = job;
    $[6] = jobType;
    $[7] = toggleJobsCard;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
};